import { useEffect, useState } from 'react'
import { CurrentPolicyPanel } from './CurrentPolicyPanel'
import { useAuth } from '@/contexts/AuthContext'
import {
  pendingPolicyCommands,
  type PendingPolicyPage,
  type PendingPolicyCursor,
  policyActions,
  readPolicyHistory,
  rememberPolicyCommand,
  sendPolicyCommand,
  recoverPolicyCommand,
  type PolicyCommand,
  type PolicyHistory,
  type PolicyReply,
  type PolicyRule,
} from '@/services/platform/policyGovernance'

interface RuleDraft {
  id: string
  subject: string
  capacity: string
  role: string
  actions: string[]
  evidence: string
  seconds: string
}
const emptyRule = (): RuleDraft => ({
  id: '',
  subject: '',
  capacity: '',
  role: '',
  actions: [],
  evidence: '',
  seconds: '',
})
const digest = /^[0-9a-f]{64}$/
const identifier = /^[A-Za-z][A-Za-z0-9._-]{0,79}$/
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function PolicyGovernancePage() {
  const { user, hasPermission } = useAuth()
  const submitter =
    user?.role === 'cml_operations' && hasPermission('txh.platform.legal_control.policy.submit')
  const reviewer =
    hasPermission('txh.platform.legal_control.policy.retire') && user?.role === 'cml_admin'
  const [pendingPage, setPendingPage] = useState<PendingPolicyPage | null>(null)
  const [history, setHistory] = useState<PolicyHistory[]>([])
  const [policyKey, setPolicyKey] = useState('')
  const [version, setVersion] = useState('')
  const [reference, setReference] = useState('')
  const [evidence, setEvidence] = useState('')
  const [effective, setEffective] = useState('')
  const [expires, setExpires] = useState('')
  const [rules, setRules] = useState<RuleDraft[]>([emptyRule()])
  const [policyId, setPolicyId] = useState('')
  const [bodyHash, setBodyHash] = useState('')
  const [decision, setDecision] = useState('approve')
  const [reason, setReason] = useState('withdrawn')
  const [lookup, setLookup] = useState('')
  const [frozen, setFrozen] = useState<PolicyCommand | null>(null)
  const [reply, setReply] = useState<PolicyReply | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [sent, setSent] = useState(false)
  useEffect(() => {
    setHistory(user ? readPolicyHistory(user.id) : [])
    setPendingPage(null)
    setFrozen(null)
    setReply(null)
    setSent(false)
  }, [user?.id])
  if (!user) return null
  if (user.tenantId || (user.authScope && user.authScope !== 'platform'))
    return <p role="alert">Use a platform session for policy governance.</p>
  const locked = sent && reply?.status !== 'projected'
  const updateRule = (index: number, change: Partial<RuleDraft>) =>
    setRules(values => values.map((value, i) => (i === index ? { ...value, ...change } : value)))
  const prepare = (action: PolicyCommand['action']) => {
    try {
      if (locked)
        throw new Error('Recover the unconfirmed command before preparing another command.')
      setError('')
      setReply(null)
      if (!digest.test(evidence))
        throw new Error(
          'Enter the sealed evidence SHA-256 digest (64 lowercase hexadecimal characters).'
        )
      let data: Record<string, unknown>
      let target = policyId
      if (action === 'submit') {
        if (
          !identifier.test(policyKey) ||
          !Number.isInteger(Number(version)) ||
          Number(version) < 1 ||
          Number(version) > 2147483647 ||
          reference.length < 8 ||
          reference.length > 256 ||
          !reference.trim() ||
          reference !== reference.trim()
        )
          throw new Error(
            'Complete the policy key, positive version and sealed evidence reference.'
          )
        const start = new Date(effective)
        const end = new Date(expires)
        if (
          !Number.isFinite(start.getTime()) ||
          !Number.isFinite(end.getTime()) ||
          end <= start ||
          end <= new Date()
        )
          throw new Error('Enter a valid effective time and later expiry.')
        const parsed: PolicyRule[] = rules.map(value => {
          const categories = value.evidence
            .split(',')
            .map(v => v.trim())
            .filter(Boolean)
          if (
            !identifier.test(value.id) ||
            !identifier.test(value.capacity) ||
            !['business', 'HUF'].includes(value.subject) ||
            !['legal_controller', 'representative'].includes(value.role) ||
            !value.actions.length ||
            !categories.length ||
            categories.length > 10 ||
            categories.some(v => !identifier.test(v)) ||
            new Set(categories).size !== categories.length ||
            !Number.isInteger(Number(value.seconds)) ||
            Number(value.seconds) < 1 ||
            Number(value.seconds) > 2147483647
          )
            throw new Error(
              'Complete every rule, with explicit actions, evidence categories and maximum authority duration.'
            )
          return {
            rule_id: value.id,
            subject_type: value.subject as PolicyRule['subject_type'],
            capacity: value.capacity,
            controller_role: value.role as PolicyRule['controller_role'],
            allowed_actions: value.actions,
            required_evidence_types: categories,
            maximum_authority_seconds: Number(value.seconds),
            independent_acceptance_required: true,
          }
        })
        if (new Set(parsed.map(v => v.rule_id)).size !== parsed.length)
          throw new Error('Rule IDs must be unique.')
        target = crypto.randomUUID()
        data = {
          policy_key: policyKey,
          version: Number(version),
          body: { schema_version: 1, rules: parsed },
          evidence_reference: reference,
          evidence_sha256: evidence,
          effective_at: start.toISOString(),
          expires_at: end.toISOString(),
        }
      } else {
        if (!uuid.test(target) || !digest.test(bodyHash))
          throw new Error(
            'Load the original policy receipt or enter its exact policy ID and body digest.'
          )
        data = {
          expected_body_sha256: bodyHash,
          evidence_sha256: evidence,
          ...(action === 'decide' ? { decision } : { reason }),
        }
      }
      const prepared = { command_id: crypto.randomUUID(), policy_id: target, action, data }
      if (new TextEncoder().encode(JSON.stringify(prepared)).length > 73728)
        throw new Error('Policy definition exceeds the command size limit.')
      setFrozen(prepared)
      setSent(false)
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Unable to prepare policy command')
    }
  }
  const dispatch = async () => {
    if (!frozen || busy || sent) return
    setBusy(true)
    setError('')
    try {
      // A failed local save blocks dispatch;never send an untracked command.
      setHistory(rememberPolicyCommand(user.id, frozen, 'unknown'))
      setSent(true)
      const result = await sendPolicyCommand(frozen)
      setReply(result)
      setHistory(rememberPolicyCommand(user.id, frozen, result.status))
    } catch {
      setError(
        'Outcome unconfirmed. Keep this command ID and ask a current administrator to check recovery. No automatic resend was made.'
      )
    } finally {
      setBusy(false)
    }
  }
  const loadPending = async (after?: PendingPolicyCursor) => {
    if (!reviewer || busy) return
    setBusy(true)
    setError('')
    try {
      setPendingPage(await pendingPolicyCommands(after))
    } catch {
      setError(
        'Pending discovery unavailable. Use a current administrator session with fresh MFA. Existing command outcomes are unchanged.'
      )
    } finally {
      setBusy(false)
    }
  }
  const recover = async (id: string) => {
    if (busy) return
    setBusy(true)
    setError('')
    try {
      const result = await recoverPolicyCommand(id)
      setReply(result)
      if (result.receipt) {
        setPolicyId(result.policy_id)
        setBodyHash(result.receipt.policy_body_sha256)
      }
      const known = history.find(item => item.commandId === id)
      if (known)
        setHistory(
          rememberPolicyCommand(
            user.id,
            { command_id: id, policy_id: known.policyId, action: known.action, data: {} },
            result.status
          )
        )
    } catch {
      setError(
        'Recovery unavailable. Check the original ID and use a current platform administrator session with fresh MFA. The command may still be pending.'
      )
    } finally {
      setBusy(false)
    }
  }
  const field = (label: string, value: string, setter: (value: string) => void, type = 'text') => (
    <label className="block text-sm">
      {label}
      <input
        className="input w-full"
        type={type}
        value={value}
        onChange={event => setter(event.target.value)}
      />
    </label>
  )
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold">Legal control policies</h1>
        <p>
          Declare evidence rules for platform review. A recorded policy does not appoint a
          controller or grant client access.
        </p>
        <p className="text-sm">
          Policy decisions require a different reviewer and fresh MFA. Unconfirmed commands keep
          their original IDs.
        </p>
      </header>
      {error && (
        <div role="alert" className="rounded bg-red-50 p-3 text-red-800">
          {error}
        </div>
      )}
      {reviewer && <CurrentPolicyPanel key={user?.id} />}
      {reviewer && (
        <section className="card p-5 space-y-3">
          <h2 className="text-lg font-semibold">Pending commands on the server</h2>
          <p>
            Find commands whose outcomes still need confirmation,including commands missing from
            this browser. Results are live;refresh the first page after changes.
          </p>
          <button className="btn" disabled={busy} onClick={() => void loadPending()}>
            Refresh pending commands
          </button>
          {pendingPage && (
            <>
              {pendingPage.items.length === 0 && <p>No pending commands on this page.</p>}
              {pendingPage.items.map(item => (
                <div key={item.command_id} className="border rounded p-3 space-y-1">
                  <p>
                    {item.action} · {item.recorded_at}
                  </p>
                  <p className="break-all">Command: {item.command_id}</p>
                  <p className="break-all">Policy: {item.policy_id}</p>
                  <button
                    className="btn"
                    disabled={busy}
                    onClick={() => void recover(item.command_id)}
                  >
                    Check server command {item.command_id}
                  </button>
                </div>
              ))}
              {pendingPage.next_cursor && (
                <button
                  className="btn"
                  disabled={busy}
                  onClick={() => void loadPending(pendingPage.next_cursor!)}
                >
                  Next pending page
                </button>
              )}
            </>
          )}
        </section>
      )}
      <section className="card p-5 space-y-3">
        <h2 className="text-lg font-semibold">Recover a command</h2>
        {field('Original command ID', lookup, setLookup)}
        <button className="btn" disabled={!reviewer || busy} onClick={() => void recover(lookup)}>
          Check recovery
        </button>
        {!reviewer && (
          <p className="text-sm">
            A current platform administrator can recover a pending command using its ID.
          </p>
        )}
      </section>
      <section className="card p-5 space-y-4">
        <h2 className="text-lg font-semibold">
          {submitter ? 'Declare a policy' : 'Review a policy'}
        </h2>
        {field('Sealed evidence SHA-256', evidence, setEvidence)}
        {submitter && (
          <>
            <div className="grid gap-3 md:grid-cols-2">
              {field('Policy key', policyKey, setPolicyKey)}
              {field('Policy version', version, setVersion, 'number')}
              {field('Sealed evidence reference', reference, setReference)}
              {field('Effective time', effective, setEffective, 'datetime-local')}
              {field('Expiry time', expires, setExpires, 'datetime-local')}
            </div>
            {rules.map((rule, index) => (
              <fieldset key={index} className="border rounded p-4 space-y-3">
                <legend>Rule {index + 1}</legend>
                <div className="grid gap-3 md:grid-cols-2">
                  {field('Rule ID', rule.id, id => updateRule(index, { id }))}
                  {field('Legal capacity', rule.capacity, capacity =>
                    updateRule(index, { capacity })
                  )}
                  <label>
                    Subject type
                    <select
                      className="input w-full"
                      value={rule.subject}
                      onChange={e => updateRule(index, { subject: e.target.value })}
                    >
                      <option value="">Choose subject</option>
                      <option value="business">Business</option>
                      <option value="HUF">HUF</option>
                    </select>
                  </label>
                  <label>
                    Controller role
                    <select
                      className="input w-full"
                      value={rule.role}
                      onChange={e => updateRule(index, { role: e.target.value })}
                    >
                      <option value="">Choose role</option>
                      <option value="legal_controller">Legal controller</option>
                      <option value="representative">Representative</option>
                    </select>
                  </label>
                  {field('Required evidence categories (comma separated)', rule.evidence, value =>
                    updateRule(index, { evidence: value })
                  )}
                  {field(
                    'Maximum authority duration (seconds)',
                    rule.seconds,
                    seconds => updateRule(index, { seconds }),
                    'number'
                  )}
                </div>
                <div className="grid gap-2 md:grid-cols-2">
                  {policyActions.map(action => (
                    <label key={action} className="text-sm">
                      <input
                        type="checkbox"
                        checked={rule.actions.includes(action)}
                        onChange={event =>
                          updateRule(index, {
                            actions: event.target.checked
                              ? [...rule.actions, action]
                              : rule.actions.filter(v => v !== action),
                          })
                        }
                      />{' '}
                      {action.split('.').join(' · ')}
                    </label>
                  ))}
                </div>
                {rules.length > 1 && (
                  <button
                    className="btn"
                    onClick={() => setRules(values => values.filter((_, i) => i !== index))}
                  >
                    Remove rule {index + 1}
                  </button>
                )}
              </fieldset>
            ))}
            <button
              className="btn"
              disabled={rules.length >= 32}
              onClick={() => setRules(values => [...values, emptyRule()])}
            >
              Add rule
            </button>
            <button
              className="btn btn-primary"
              disabled={busy || locked}
              onClick={() => prepare('submit')}
            >
              Prepare declaration
            </button>
          </>
        )}
        {reviewer && (
          <>
            <div className="grid gap-3 md:grid-cols-2">
              {field('Policy ID', policyId, setPolicyId)}
              {field('Original policy body SHA-256', bodyHash, setBodyHash)}
            </div>
            <label>
              Decision
              <select
                className="input"
                value={decision}
                onChange={e => setDecision(e.target.value)}
              >
                <option value="approve">Approve</option>
                <option value="reject">Reject</option>
              </select>
            </label>
            <button
              className="btn btn-primary"
              disabled={busy || locked}
              onClick={() => prepare('decide')}
            >
              Prepare decision
            </button>
            <label>
              Retirement reason
              <select className="input" value={reason} onChange={e => setReason(e.target.value)}>
                <option value="withdrawn">Withdrawn</option>
                <option value="superseded">Superseded</option>
                <option value="incorrect_policy">Incorrect policy</option>
              </select>
            </label>
            <button className="btn" disabled={busy || locked} onClick={() => prepare('retire')}>
              Prepare retirement
            </button>
          </>
        )}
      </section>
      {frozen && (
        <section className="card p-5 space-y-3">
          <h2 className="font-semibold">Prepared {frozen.action}</h2>
          <p>
            Command ID: <code>{frozen.command_id}</code>
          </p>
          <p>
            Policy ID: <code>{frozen.policy_id}</code>
          </p>
          <PolicyDetails command={frozen} />
          <p>
            The prepared command keeps these IDs. Changes to the form require a new preparation
            before sending.
          </p>
          <button
            className="btn btn-primary"
            disabled={busy || sent}
            onClick={() => void dispatch()}
          >
            Send prepared command
          </button>
          {sent && (
            <p>Sent or unconfirmed. Check recovery using this ID;this page will not resend it.</p>
          )}
        </section>
      )}
      {reply && (
        <section className="card p-5 space-y-2" aria-label="Policy outcome">
          <h2 className="font-semibold">
            {reply.status === 'projected' ? 'Policy history recorded' : 'Pending recovery'}
          </h2>
          <p>Command ID: {reply.command_id}</p>
          <p>Policy ID: {reply.policy_id}</p>
          {reply.receipt && (
            <>
              <p>Action: {reply.receipt.action}</p>
              <PolicyDetails command={reply.receipt.request} />
              <p>Recorded by: {reply.receipt.actor_user_id}</p>
              <p>Committed: {reply.receipt.committed_at}</p>
              <p>
                Policy body SHA-256: <code>{reply.receipt.policy_body_sha256}</code>
              </p>
            </>
          )}
          <p>Client authority requires a separate verified appointment.</p>
        </section>
      )}
      <section className="card p-5 space-y-3">
        <h2 className="font-semibold">Command IDs on this browser</h2>
        <p className="text-sm">
          Only IDs, action and last observed status are saved for this account. Server history
          remains authoritative.
        </p>
        {history.length === 0 && <p>No saved command IDs.</p>}
        {history.map(item => (
          <article key={item.commandId} className="border-t py-2">
            <p>
              {item.action} · {item.status}
            </p>
            <code>{item.commandId}</code>
            {reviewer && (
              <button
                className="btn ml-3"
                disabled={busy}
                onClick={() => void recover(item.commandId)}
              >
                Recover this command
              </button>
            )}
          </article>
        ))}
      </section>
    </div>
  )
}

function PolicyDetails({ command }: { command: PolicyCommand }) {
  const data = command.data
  if (command.action !== 'submit')
    return (
      <dl>
        <dt>Expected policy body digest</dt>
        <dd>
          <code>{String(data.expected_body_sha256)}</code>
        </dd>
        <dt>{command.action === 'decide' ? 'Decision' : 'Retirement reason'}</dt>
        <dd>{String(data.decision ?? data.reason)}</dd>
        <dt>Evidence digest</dt>
        <dd>
          <code>{String(data.evidence_sha256)}</code>
        </dd>
      </dl>
    )
  const body = data.body as { rules?: PolicyRule[] }
  return (
    <div className="space-y-2">
      <p>
        {String(data.policy_key)} · version {String(data.version)}
      </p>
      <p>
        Effective: {String(data.effective_at)} · Expiry: {String(data.expires_at)}
      </p>
      <p>Evidence reference: {String(data.evidence_reference)}</p>
      <p>
        Evidence digest: <code>{String(data.evidence_sha256)}</code>
      </p>
      {body?.rules?.map(rule => (
        <article className="border rounded p-3" key={rule.rule_id}>
          <h3 className="font-semibold">{rule.rule_id}</h3>
          <p>
            {rule.subject_type} · {rule.capacity} · {rule.controller_role}
          </p>
          <p>Allowed actions: {rule.allowed_actions.join(', ')}</p>
          <p>Required evidence: {rule.required_evidence_types.join(', ')}</p>
          <p>
            Maximum authority: {rule.maximum_authority_seconds} seconds · Independent acceptance
            required
          </p>
        </article>
      ))}
    </div>
  )
}
